/**
 * CertificationTree — la tabella ad albero della certificazione del chiamante.
 *
 * Sostituisce il pannello a tre radio + lista di checkbox: l'operatore vede
 * cliente → edificio → impianto, cerca, ordina, e spunta ai tre livelli. La
 * selezione e' la **fotografia** dello stato desiderato, non un'aggiunta:
 * togliere una spunta toglie davvero, e togliere tutte le spunte a un cliente
 * e' una revoca — dichiarata in `revoke`, mai dedotta da una lista vuota
 * (nel modello "lista vuota" significa *nessuna restrizione*, cioe' il
 * contrario di quello che l'operatore crederebbe).
 *
 * Le tre verita' che il componente deve dire, e non lasciar dedurre:
 *
 * 1. **Cosa e' gia' concesso.** Il pannello si mostra anche quando il numero
 *    e' gia' certificato (prima si nascondeva, e un numero certificato su 1
 *    edificio su 61 non era piu' governabile). Lo stato di oggi e' il punto di
 *    partenza delle spunte, ed e' scritto in chiaro accanto al cliente.
 * 2. **"Edificio intero" ≠ "tutti gli impianti spuntati".** Il primo comprende
 *    gli impianti che entreranno domani in anagrafica, il secondo li esclude.
 *    Sembrano identici all'occhio: quindi la differenza e' scritta a parole su
 *    ogni riga, e viaggia nel payload (`all_assets: true` contro lista).
 * 3. **Quello che si vede non e' tutto.** `truncated`/`total_buildings` sono
 *    sempre visibili: un elenco tagliato che sembra completo fa approvare
 *    un'ampiezza diversa da quella creduta.
 *
 * L'anagrafica non ha alcun campo di stato sugli impianti: non si puo' sapere
 * se un impianto e' dismesso, e l'interfaccia lo dice invece di far credere
 * che l'elenco sia una lista di impianti attivi.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchCertificationAssets, fetchCertificationTree } from "../api/client";
import type {
  CertificationAsset,
  ConversationByRequest,
  ConversazioneTranscriptMessage,
  CertificationTreeBuilding,
  CertificationTreeCustomer,
  CertificationTreeResponse,
  CertifyCallerPayload,
  CertifyCustomerRef,
  CertifyTreeBuilding,
  CertifyTreeCustomer,
} from "../api/types";

/* ── Contratto verso la pagina ───────────────────────────────────────── */

export interface CertificationChoice {
  /** Da allegare ad `approveRequest`; null ⇒ nessuna modifica alla certificazione. */
  payload: CertifyCallerPayload | null;
  /** Riassunto di una riga per il percorso riprocesso; null se non si certifica. */
  summary: string | null;
  /** false ⇒ scelta incompleta o non confermata: l'approvazione va bloccata. */
  valid: boolean;
  /* Perche' e' bloccata, in chiaro. Non basta che stia scritto nel pannello:
     su un cliente da 645 edifici l'avviso puo' finire migliaia di pixel sopra
     o sotto il viewport, e allora l'unico segnale resta il tasto grigio, con il
     motivo in un `title` che si vede solo passandoci il mouse. La pagina lo
     ripete nella barra sticky, accanto al tasto: l'unico posto che l'operatore
     ha sempre davanti. */
  blockers: string[];
}

/** Stato neutro: nessuna certificazione, nessun blocco. */
export const NO_CERTIFICATION: CertificationChoice = { payload: null, summary: null, valid: true, blockers: [] };

interface CertificationTreeProps {
  requestId: string;
  /** Pubblica la scelta corrente. Deve essere stabile (es. un `setState`). */
  onChange: (choice: CertificationChoice) => void;
  /* Pubblica se il pannello e' **aperto**, cioe' se sta mostrando la tabella:
     e' il segnale con cui la pagina gli da' la larghezza piena. Deve essere
     stabile (es. un `setState`). */
  onExpandedChange?: (expanded: boolean) => void;
  /* Conversazione della pratica, per l'estratto in testa al pannello. Arriva
     dalla pagina, che la carica gia': una seconda fetch qui darebbe due
     versioni della stessa sessione. */
  conversation?: ConversationByRequest | null;
  /** Rimando alla conversazione completa, che nella pagina esiste gia'. */
  onOpenConversation?: () => void;
}

/** Stili dei tre riquadri della griglia di Review3, nei due stati del pannello. */
export interface CertificationLayout {
  panel: React.CSSProperties;
  left: React.CSSProperties;
  right: React.CSSProperties;
}

/* ── Piazzamento nella griglia della pagina ──────────────────────────────
   La certificazione e' una decisione che si prende **prima** di guardare la
   pratica, quindi quando la tabella e' aperta il pannello prende la larghezza
   piena sopra le due colonne; chiuso, resta dov'e' oggi, in cima alla colonna
   di sinistra.

   Il piazzamento sta qui, in una funzione sola, per una ragione precisa: e' un
   contratto a tre pezzi, e il modo in cui questo genere di cambio si rompe e'
   spostarne uno e dimenticare gli altri. Se il pannello occupa entrambe le
   colonne ma la colonna dell'email resta in riga 1, l'email finisce **accanto**
   al pannello e sotto le rimane un buco: la pagina si scompone. Le tre regole
   vanno lette insieme, e il test le verifica insieme.

   `minWidth: 0` su tutti e tre non e' cosmetico: dentro il pannello c'e' una
   tabella con `overflow-x: auto`, e senza questo la larghezza minima del
   contenuto sfonda la colonna della griglia (grid blowout) invece di far
   scorrere la tabella. */
export function certificationGridPlacement(expanded: boolean): CertificationLayout {
  return expanded
    ? {
      panel: { minWidth: 0, gridColumn: "1 / -1", gridRow: "1" },
      left: { minWidth: 0, gridColumn: "1", gridRow: "2" },
      // In riga 2 anche l'email: la riga 1 e' tutta del pannello.
      right: { minWidth: 0, gridColumn: "2", gridRow: "2" },
    }
    : {
      panel: { minWidth: 0, gridColumn: "1", gridRow: "1" },
      left: { minWidth: 0, gridColumn: "1", gridRow: "2" },
      // Il pannello occupa solo la colonna 1: l'email parte da riga 1 e copre
      // entrambe le righe, cioe' resta in cima esattamente come oggi.
      right: { minWidth: 0, gridColumn: "2", gridRow: "1 / span 2" },
    };
}

/* ── Selezione ───────────────────────────────────────────────────────────
   Due livelli espliciti, perche' sono due concessioni diverse:
   `allAssets` = l'edificio per intero (impianti futuri compresi);
   `assets`    = la lista congelata degli impianti scelti. */

interface BuildingSel {
  allAssets: boolean;
  /** Significativo solo con `allAssets === false`. Mai vuoto: vuoto ⇒ l'edificio esce. */
  assets: string[];
}

interface CustomerSel {
  /** true ⇒ tutto il cliente, nessuna restrizione. */
  allBuildings: boolean;
  buildings: Record<string, BuildingSel>;
}

/** Riga della tabella edifici: `pinned` viene dal server, `local` dal client. */
type BuildingRow = CertificationTreeBuilding & { pinned: boolean; local: boolean };

type TriState = "on" | "off" | "mixed";
type SortState = { key: string; dir: "asc" | "desc" } | null;

const PAGE_LIMIT = 100;
const SEARCH_DEBOUNCE_MS = 300;

const custKey = (c: CertifyCustomerRef): string => `${c.codaz}|${c.codcli_for}`;
const bKey = (k: string, edificio: string): string => `${k}|${edificio}`;

function nextSort(cur: SortState, key: string): SortState {
  if (!cur || cur.key !== key) return { key, dir: "asc" };
  return cur.dir === "asc" ? { key, dir: "desc" } : null;
}

function cmp(a: unknown, b: unknown): number {
  const na = typeof a === "number" ? a : Number(String(a ?? "").replace(",", "."));
  const nb = typeof b === "number" ? b : Number(String(b ?? "").replace(",", "."));
  if (!Number.isNaN(na) && !Number.isNaN(nb) && String(a ?? "").trim() !== "" && String(b ?? "").trim() !== "") {
    return na - nb;
  }
  return String(a ?? "").localeCompare(String(b ?? ""), "it", { sensitivity: "base" });
}

function sortRows<T>(rows: T[], sort: SortState, pick: (row: T, key: string) => unknown): T[] {
  if (!sort) return rows;
  const out = [...rows];
  out.sort((x, y) => cmp(pick(x, sort.key), pick(y, sort.key)) * (sort.dir === "asc" ? 1 : -1));
  return out;
}

/* La potenza in anagrafica vale `0` sulla quasi totalita' degli impianti e,
   quando c'e', arriva senza unita' di misura: una colonna di zeri e di numeri
   nudi non aiuta a riconoscere un impianto, e occupava spazio che serviva alle
   colonne che lo identificano. Resta come nota accanto al tipo quando dice
   qualcosa, e senza inventare un'unita' che il dato non ha. */
function potenzaLabel(a: CertificationAsset): string | null {
  const n = Number(String(a.potenza ?? "").replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return null;
  return `pot. ${a.potenza}`;
}

/* Estratto della conversazione. Aprendo il pannello a piena larghezza la
   colonna della conversazione scende fuori schermo, e l'operatore decide *cosa
   potra' vedere questo numero* senza piu' avere sotto gli occhi *cosa ha detto
   il chiamante* — che in un pannello che concede autorizzazioni e' l'unica
   prova di chi sia.

   Si mostrano **solo i turni del chiamante**, e non gli ultimi in ordine di
   tempo: gli ultimi tre turni di una conversazione reale sono tipicamente
   "agente / chiamante / agente", e il messaggio in cui il chiamante dice chi e'
   — nome, indirizzo, impianto — e' quasi sempre il **primo**. Prendere la coda
   cronologica lasciava fuori l'unica cosa che serve a decidere, e mostrava un
   "si confermo" fra due risposte dell'agente sotto il titolo "cosa ha detto il
   chiamante": una promessa che l'interfaccia non manteneva.

   Quindi: il primo turno sostanzioso del chiamante piu' i suoi ultimi due, in
   ordine di tempo, e i salti dichiarati invece di far leggere come consecutivi
   turni che non lo sono. Le risposte dell'agente sono contesto, non prova:
   stanno nella conversazione completa, a un click.

   Funzione pura e non un hook, di proposito: si usa sotto l'early return del
   componente, e un `useMemo` la' dentro cambierebbe il numero di hook fra un
   render e l'altro. */

interface CallerTurns {
  rows: Array<{ msg: ConversazioneTranscriptMessage; omittedAfter: number }>;
  /** Quanti messaggi ha mandato in tutto: serve a dire "3 dei suoi 7". */
  total: number;
}

/** Soglia oltre la quale un turno del chiamante si considera sostanzioso. */
const SUBSTANTIAL_TURN_CHARS = 20;

export function pickCallerTurns(
  conversation: ConversationByRequest | null | undefined,
): CallerTurns {
  const inbound = (conversation?.transcript ?? []).filter(
    (m) => m.direction === "inbound" && (m.normalized_text ?? "").trim().length > 0,
  );
  if (inbound.length === 0) return { rows: [], total: 0 };

  const substantial = inbound.find(
    (m) => (m.normalized_text ?? "").trim().length >= SUBSTANTIAL_TURN_CHARS,
  ) ?? inbound[0];
  const keep = new Set([substantial, ...inbound.slice(-2)]);
  const picked = inbound.filter((m) => keep.has(m));

  const rows = picked.map((m, i) => {
    const here = inbound.indexOf(m);
    const next = i + 1 < picked.length ? inbound.indexOf(picked[i + 1]) : here + 1;
    return { msg: m, omittedAfter: Math.max(0, next - here - 1) };
  });
  return { rows, total: inbound.length };
}

/* ── Checkbox a tre stati ──────────────────────────────────────────────── */

function TriCheckbox({
  state, onToggle, disabled, label, title,
}: {
  state: TriState;
  onToggle: () => void;
  disabled?: boolean;
  label: string;
  title?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === "mixed";
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label={label}
      title={title}
      checked={state === "on"}
      disabled={disabled}
      onChange={onToggle}
      onClick={(e) => e.stopPropagation()}
      style={{ accentColor: "var(--indaco-brand-primary)", cursor: disabled ? "not-allowed" : "pointer" }}
    />
  );
}

function SortIcon({ sort, sortKey }: { sort: SortState; sortKey: string }) {
  const active = sort?.key === sortKey;
  return (
    <span
      className={`material-symbols-outlined${active ? "" : " th-icon-hover"}`}
      /* Blu solo se l'ordinamento e' attivo: un'icona blu su ogni intestazione
         fa sembrare cliccabile anche una colonna che non lo e' — su "Imp.",
         stretta, l'effetto era una colonna che pareva un link. */
      style={{ fontSize: 13, color: active ? "var(--indaco-brand-primary)" : "var(--indaco-gray-500)", opacity: active ? 1 : 0.7 }}
    >
      {active ? (sort!.dir === "asc" ? "arrow_upward" : "arrow_downward") : "swap_vert"}
    </span>
  );
}

/* Intestazione ordinabile. `second` esiste per una ragione di spazio: il
   pannello vive in una colonna da ~660px, e tenere una colonna per campo
   mandava descrizioni e indirizzi a capo su 4-5 righe. I campi secondari
   stanno sulla seconda riga della cella, e il loro ordinamento resta
   raggiungibile da qui invece di sparire con la colonna. */
function SortTh({
  label, sortKey, sort, setSort, style, second,
}: {
  label: string;
  sortKey: string;
  sort: SortState;
  setSort: (s: SortState) => void;
  style?: React.CSSProperties;
  second?: { label: string; sortKey: string };
}) {
  return (
    <th style={{ padding: "4px 6px", fontSize: 10, ...style }}>
      <div style={{ display: "flex", alignItems: "center", gap: 2, userSelect: "none" }}>
        <span
          style={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }}
          onClick={() => setSort(nextSort(sort, sortKey))}
          role="button"
          aria-label={`Ordina per ${label}`}
        >
          {label}
          <SortIcon sort={sort} sortKey={sortKey} />
        </span>
        {second && (
          <span
            style={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer", fontWeight: 400, color: "var(--indaco-gray-600)" }}
            onClick={() => setSort(nextSort(sort, second.sortKey))}
            role="button"
            aria-label={`Ordina per ${second.label}`}
          >
            {second.label}
            <SortIcon sort={sort} sortKey={second.sortKey} />
          </span>
        )}
      </div>
    </th>
  );
}

/* ── Componente ──────────────────────────────────────────────────────── */

interface AssetsState {
  rows: CertificationAsset[];
  truncated: boolean;
  loading: boolean;
  error: string | null;
}

export function CertificationTree({
  requestId, onChange, onExpandedChange, conversation, onOpenConversation,
}: CertificationTreeProps) {
  const [data, setData] = useState<CertificationTreeResponse | null>(null);
  const [certify, setCertify] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [openCust, setOpenCust] = useState<Set<string>>(new Set());
  const [openBuild, setOpenBuild] = useState<Set<string>>(new Set());
  const [assets, setAssets] = useState<Record<string, AssetsState>>({});
  const [assetQuery, setAssetQuery] = useState<Record<string, string>>({});
  const [assetNotice, setAssetNotice] = useState<Record<string, string | null>>({});

  const [sel, setSel] = useState<Record<string, CustomerSel>>({});
  /* Edifici toccati a mano: su questi l'attribuzione automatica degli impianti
     gia' concessi non deve piu' intervenire, altrimenti rimetterebbe le spunte
     che l'operatore ha appena tolto. */
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [revokeOk, setRevokeOk] = useState<Set<string>>(new Set());
  /* Rinuncia esplicita agli impianti gia' concessi che non si riesce ad
     attribuire a un edificio dell'elenco: senza questa spunta la selezione e'
     bloccata, perche' altrimenti la ricertificazione li perderebbe in silenzio. */
  const [dropUnresolved, setDropUnresolved] = useState<Set<string>>(new Set());
  /* Esito dell'ultima azione di massa. Senza, l'unica prova che il click ha
     fatto qualcosa sono le spunte, che su cento righe non si abbracciano con
     lo sguardo. Si azzera appena l'operatore tocca qualcosa a mano. */
  const [bulkFeedback, setBulkFeedback] = useState<Record<string, string | null>>({});
  /** Codici impianto di cui conosciamo l'edificio (righe caricate o `granted_assets`). */
  const [resolved, setResolved] = useState<Record<string, string[]>>({});

  /* Anagrafica di riga vista almeno una volta, per codice. Serve quando un
     edificio spuntato esce dal filtro corrente: la riga va tenuta a schermo, e
     senza questa cache la si mostrerebbe con descrizione, indirizzo, comune e
     numero impianti tutti a "—" — cioe' con il solo codice, che e' proprio
     l'informazione che non basta a riconoscerlo. */
  const [meta, setMeta] = useState<Record<string, CertificationTreeBuilding>>({});

  const [bSort, setBSort] = useState<SortState>(null);
  const [aSort, setASort] = useState<SortState>(null);

  const seeded = useRef(false);

  /* ── Ricerca sugli edifici: debounce + server. Un cliente reale arriva a 644
        edifici, l'elenco non si puo' servire tutto al client. ── */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  /* Attribuisce a un edificio gli impianti gia' concessi che gli appartengono.
     Serve a due cose: mostrare la concessione di oggi e non perderla quando la
     selezione viene riscritta in modo autorevole. */
  const attribute = useCallback((k: string, edificio: string, codes: string[], granted: string[]) => {
    const mine = codes.filter((c) => granted.includes(c));
    setResolved((prev) => {
      const known = prev[k] ?? [];
      const add = mine.filter((c) => !known.includes(c));
      return add.length ? { ...prev, [k]: [...known, ...add] } : prev;
    });
    if (mine.length === 0) return;
    setSel((prev) => {
      const cur = prev[k];
      if (!cur || cur.allBuildings) return prev;
      if (touched.has(bKey(k, edificio))) return prev;
      const existing = cur.buildings[edificio];
      if (existing?.allAssets) return prev;
      const merged = [...new Set([...(existing?.assets ?? []), ...mine])];
      if (existing && merged.length === existing.assets.length) return prev;
      return { ...prev, [k]: { ...cur, buildings: { ...cur.buildings, [edificio]: { allAssets: false, assets: merged } } } };
    });
  }, [touched]);

  /* ── Caricamento dell'albero. L'endpoint puo' non esistere (backend piu'
        vecchio) o negare il permesso: in quel caso il pannello non compare e
        l'approvazione resta quella di sempre. ── */
  useEffect(() => {
    if (!requestId) { setData(null); return; }
    let alive = true;
    setLoading(true);
    fetchCertificationTree(requestId, { q: debouncedQ || undefined, offset: 0, limit: PAGE_LIMIT })
      .then((d) => {
        if (!alive) return;
        setData(d);
        if (!seeded.current) {
          seeded.current = true;
          const initial: Record<string, CustomerSel> = {};
          const known: Record<string, string[]> = {};
          for (const c of d.customers) {
            const k = custKey(c);
            const g = c.granted;
            const buildings: Record<string, BuildingSel> = {};
            if (g.certified && !g.all_buildings) {
              for (const b of g.buildings) buildings[b] = { allAssets: true, assets: [] };
              for (const row of c.buildings) {
                const mine = (row.granted_assets ?? []).filter((a) => g.assets.includes(a));
                if (mine.length === 0) continue;
                known[k] = [...(known[k] ?? []), ...mine];
                if (!buildings[row.codice_edificio]) {
                  buildings[row.codice_edificio] = { allAssets: false, assets: [...mine] };
                }
              }
            }
            initial[k] = { allBuildings: Boolean(g.certified && g.all_buildings), buildings };
          }
          setSel(initial);
          setResolved(known);
        }
      })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [requestId, debouncedQ]);

  /* Impianti di un edificio: si caricano quando la riga si apre. Il massimo
     reale e' 76 per edificio, quindi da qui in giu' la ricerca resta locale. */
  useEffect(() => {
    if (!data) return;
    for (const c of data.customers) {
      const k = custKey(c);
      const dead = c.granted.unknown_assets ?? [];
      const live = c.granted.assets.filter((a) => !dead.includes(a));
      const opened = [...openBuild].filter((x) => x.startsWith(`${k}|`)).map((x) => x.slice(k.length + 1));
      /* Le righe che il server ha fissato *perche' contengono un impianto
         concesso* si risolvono da sole, in background: l'attribuzione e' un
         fatto, non un compito da dare all'operatore. Senza questo, la riga
         c'e' ma la spunta no, e la guardia sugli impianti non attribuiti lo
         costringerebbe ad aprirle una per una per sbloccare l'approvazione. */
      const toResolve = live.length > 0
        ? c.buildings
          .filter((b) => b.pinned && !c.granted.buildings.includes(b.codice_edificio))
          .map((b) => b.codice_edificio)
        : [];
      for (const edificio of [...new Set([...opened, ...toResolve])]) {
        const key = bKey(k, edificio);
        if (assets[key]) continue;
        setAssets((prev) => ({ ...prev, [key]: { rows: [], truncated: false, loading: true, error: null } }));
        fetchCertificationAssets(requestId, { codaz: c.codaz, codcli_for: c.codcli_for, edificio })
          .then((res) => {
            setAssets((prev) => ({ ...prev, [key]: { rows: res.assets, truncated: res.truncated, loading: false, error: null } }));
            attribute(k, edificio, res.assets.map((a) => a.codice_impianto), c.granted.assets);
          })
          .catch((e) => {
            setAssets((prev) => ({
              ...prev,
              [key]: { rows: [], truncated: false, loading: false, error: e instanceof Error ? e.message : "Errore" },
            }));
          });
      }
    }
  }, [openBuild, data, requestId, assets, attribute]);

  useEffect(() => {
    if (!data) return;
    setMeta((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const c of data.customers) {
        for (const b of c.buildings) {
          const key = bKey(custKey(c), b.codice_edificio);
          if (!next[key]) { next[key] = b; changed = true; }
        }
      }
      return changed ? next : prev;
    });
  }, [data]);

  const customers = useMemo(() => data?.customers ?? [], [data]);

  /* ── Righe edificio mostrate per un cliente ──────────────────────────────
     Gli edifici gia' concessi li **fissa il server** (`pinned`): arrivano
     sempre, anche quando `q`/`offset`/`limit` li escluderebbero, cosi' che
     l'operatore non perda di vista cio' che sta gia' concedendo mentre cerca.
     Al client resta un solo caso da coprire: un edificio spuntato in questa
     sessione che il filtro corrente non contiene — il server non puo' saperlo,
     e se sparisse dalla tabella l'operatore approverebbe un'ampiezza che non
     ha piu' sotto gli occhi. */
  const rowsFor = useCallback((c: CertificationTreeCustomer): BuildingRow[] => {
    const k = custKey(c);
    const listed = new Set(c.buildings.map((b) => b.codice_edificio));
    const local: BuildingRow[] = Object.keys(sel[k]?.buildings ?? {})
      .filter((code) => !listed.has(code))
      .map((code) => ({
        descrizione: null, indirizzo: null, comune: null, assets_count: null,
        ...(meta[bKey(k, code)] ?? {}),
        codice_edificio: code, pinned: false, local: true,
      }));
    return [...local, ...c.buildings.map((b) => ({ ...b, pinned: Boolean(b.pinned), local: false }))];
  }, [sel, meta]);

  /* ── Stati derivati ──────────────────────────────────────────────────── */

  const custState = useCallback((k: string): TriState => {
    const s = sel[k];
    if (!s) return "off";
    if (s.allBuildings) return "on";
    return Object.keys(s.buildings).length > 0 ? "mixed" : "off";
  }, [sel]);

  const buildState = useCallback((k: string, edificio: string): TriState => {
    const s = sel[k];
    if (!s) return "off";
    if (s.allBuildings) return "on";
    const b = s.buildings[edificio];
    if (!b) return "off";
    return b.allAssets ? "on" : b.assets.length > 0 ? "mixed" : "off";
  }, [sel]);

  const assetChecked = useCallback((k: string, edificio: string, code: string): boolean => {
    const s = sel[k];
    if (!s) return false;
    if (s.allBuildings) return true;
    const b = s.buildings[edificio];
    if (!b) return false;
    return b.allAssets || b.assets.includes(code);
  }, [sel]);

  /* Impianti concessi di cui non sappiamo ancora l'edificio. Con le righe
     fissate dal server non dovrebbe piu' capitare, ma la guardia resta: se
     capitasse, riscrivere la selezione li perderebbe in silenzio.
     **Gli impianti sconosciuti all'anagrafica sono esclusi**: quelli non
     avranno mai una riga, e confonderli con questi trasformerebbe una
     protezione in un blocco permanente. */
  const unresolvedFor = useCallback((c: CertificationTreeCustomer): string[] => {
    const k = custKey(c);
    if (!c.granted.certified || c.granted.all_buildings) return [];
    const known = resolved[k] ?? [];
    const inSel = Object.values(sel[k]?.buildings ?? {}).flatMap((b) => b.assets);
    const dead = c.granted.unknown_assets ?? [];
    return c.granted.assets.filter((a) => !dead.includes(a) && !known.includes(a) && !inSel.includes(a));
  }, [resolved, sel]);

  /* Etichette leggibili per i codici. "IMPIANTO I000197" non dice all'operatore
     che cosa vedra' il chiamante: per saperlo dovrebbe aprire l'edificio. I nomi
     degli impianti delle righe fissate li risolviamo gia' in background, quindi
     si usano; dove non si conoscono resta il codice, senza inventare nulla. */
  const assetLabel = useCallback((k: string, code: string): string => {
    for (const [key, st] of Object.entries(assets)) {
      if (!key.startsWith(`${k}|`)) continue;
      const row = st.rows.find((r) => r.codice_impianto === code);
      if (!row) continue;
      const name = [row.desc_tipo, row.descrizione].filter(Boolean).join(" ").trim();
      return name ? `${code} · ${name}` : code;
    }
    return code;
  }, [assets]);

  const buildingLabel = useCallback((k: string, code: string): string => {
    const descr = (meta[bKey(k, code)]?.descrizione ?? "").trim();
    return descr ? `${code} · ${descr}` : code;
  }, [meta]);

  /** Autorizzazioni morte: concesse, ma su impianti che l'anagrafica non ha piu'. */
  const deadFor = useCallback(
    (c: CertificationTreeCustomer): string[] => (c.granted.certified ? c.granted.unknown_assets ?? [] : []),
    [],
  );

  /* ── Azioni ──────────────────────────────────────────────────────────── */

  function toggleCustomer(k: string) {
    setBulkFeedback((p) => (p[k] ? { ...p, [k]: null } : p));
    setSel((prev) => {
      const cur = prev[k] ?? { allBuildings: false, buildings: {} };
      const state: TriState = cur.allBuildings ? "on" : Object.keys(cur.buildings).length ? "mixed" : "off";
      // "misto" sale a "tutto il cliente"; da "tutto" si scende al vuoto, che
      // per un cliente gia' certificato e' una revoca (e chiede conferma).
      const next: CustomerSel = state === "on"
        ? { allBuildings: false, buildings: {} }
        : { allBuildings: true, buildings: {} };
      return { ...prev, [k]: next };
    });
  }

  function toggleBuilding(k: string, edificio: string) {
    setBulkFeedback((p) => (p[k] ? { ...p, [k]: null } : p));
    setTouched((prev) => new Set(prev).add(bKey(k, edificio)));
    setSel((prev) => {
      const cur = prev[k] ?? { allBuildings: false, buildings: {} };
      if (cur.allBuildings) return prev; // gestito a livello cliente
      const buildings = { ...cur.buildings };
      const b = buildings[edificio];
      const state: TriState = !b ? "off" : b.allAssets ? "on" : b.assets.length ? "mixed" : "off";
      if (state === "on") delete buildings[edificio];
      // Regola dettata: spuntare l'edificio prende **tutti** i suoi impianti,
      // compresi quelli futuri; l'operatore semmai ne toglie.
      else buildings[edificio] = { allAssets: true, assets: [] };
      return { ...prev, [k]: { ...cur, buildings } };
    });
  }

  function toggleAsset(k: string, edificio: string, code: string) {
    setBulkFeedback((p) => (p[k] ? { ...p, [k]: null } : p));
    const key = bKey(k, edificio);
    const st = assets[key];
    const allCodes = (st?.rows ?? []).map((a) => a.codice_impianto);
    const cur = sel[k] ?? { allBuildings: false, buildings: {} };
    if (cur.allBuildings) return;
    const b = cur.buildings[edificio];

    if (b?.allAssets) {
      /* Si sta togliendo un impianto da un edificio concesso per intero: la
         concessione smette di essere "l'edificio" e diventa una lista, che
         **congela** l'insieme. Con l'elenco impianti troncato la lista sarebbe
         incompleta, quindi l'operazione non si fa: si dice perche'. */
      if (st?.truncated) {
        setAssetNotice((p) => ({
          ...p,
          [key]: "Elenco impianti parziale: non si può ricavare una lista esplicita completa. Cerca l'impianto o lascia l'edificio intero.",
        }));
        return;
      }
      setAssetNotice((p) => ({ ...p, [key]: null }));
      setTouched((p) => new Set(p).add(key));
      const rest = allCodes.filter((c) => c !== code);
      setSel((prev) => {
        const c0 = prev[k];
        const buildings = { ...c0.buildings };
        if (rest.length === 0) delete buildings[edificio];
        else buildings[edificio] = { allAssets: false, assets: rest };
        return { ...prev, [k]: { ...c0, buildings } };
      });
      return;
    }

    setAssetNotice((p) => ({ ...p, [key]: null }));
    setTouched((p) => new Set(p).add(key));
    setSel((prev) => {
      const c0 = prev[k] ?? { allBuildings: false, buildings: {} };
      const buildings = { ...c0.buildings };
      const prevAssets = buildings[edificio]?.assets ?? [];
      const next = prevAssets.includes(code) ? prevAssets.filter((x) => x !== code) : [...prevAssets, code];
      // Nessun impianto ⇒ l'edificio esce dalla selezione: `all_assets:false`
      // con lista vuota e' un errore di contratto, ed e' anche il caso in cui
      // "vuoto" verrebbe letto come "tutto".
      if (next.length === 0) delete buildings[edificio];
      else buildings[edificio] = { allAssets: false, assets: next };
      return { ...prev, [k]: { ...c0, buildings } };
    });
  }

  /* Spunta / despunta **gli edifici mostrati**. Su un cliente da 645 edifici
     l'unica alternativa era spuntarli a uno a uno oppure prendere tutto il
     cliente. Riguarda solo le righe a schermo, e l'etichetta lo dice: un
     "seleziona tutti" che comprendesse anche il non visto sarebbe il modo
     elegante di concedere per sbaglio, cioe' il difetto contro cui e' scritto
     tutto il resto. */
  function setShown(k: string, codes: string[], on: boolean) {
    setTouched((p) => { const n = new Set(p); codes.forEach((c) => n.add(bKey(k, c))); return n; });
    setBulkFeedback((p) => ({
      ...p,
      [k]: on
        ? `${codes.length} edifici spuntati, interi`
        : `${codes.length} edifici deselezionati`,
    }));
    setSel((prev) => {
      const cur = prev[k] ?? { allBuildings: false, buildings: {} };
      if (cur.allBuildings) return prev;
      const buildings = { ...cur.buildings };
      for (const code of codes) {
        if (on) buildings[code] = { allAssets: true, assets: [] };
        else delete buildings[code];
      }
      return { ...prev, [k]: { ...cur, buildings } };
    });
  }

  /** Promuove una lista esplicita a concessione dell'edificio intero. */
  function promoteToWholeBuilding(k: string, edificio: string) {
    setTouched((p) => new Set(p).add(bKey(k, edificio)));
    setSel((prev) => {
      const c0 = prev[k] ?? { allBuildings: false, buildings: {} };
      return { ...prev, [k]: { ...c0, buildings: { ...c0.buildings, [edificio]: { allAssets: true, assets: [] } } } };
    });
  }

  async function loadMore() {
    if (!data) return;
    setLoadingMore(true);
    try {
      // L'offset conta solo le righe paginate: quelle fissate arrivano sempre.
      const offset = Math.max(...data.customers.map((c) => c.buildings.filter((b) => !b.pinned).length), 0);
      const more = await fetchCertificationTree(requestId, { q: debouncedQ || undefined, offset, limit: PAGE_LIMIT });
      setData((prev) => {
        if (!prev) return more;
        return {
          ...prev,
          customers: prev.customers.map((c) => {
            const add = more.customers.find((m) => custKey(m) === custKey(c));
            if (!add) return c;
            // Le righe fissate tornano a ogni chiamata: senza dedup si duplicherebbero.
            const seen = new Set(c.buildings.map((b) => b.codice_edificio));
            return {
              ...c,
              buildings: [...c.buildings, ...add.buildings.filter((b) => !seen.has(b.codice_edificio))],
              total_buildings: add.total_buildings,
              truncated: add.truncated,
            };
          }),
        };
      });
    } catch { /* il conteggio resta quello di prima: nessuna selezione va persa */ }
    finally { setLoadingMore(false); }
  }

  /* ── Scelta pubblicata ───────────────────────────────────────────────── */

  /* Motivi del blocco. Si calcolano **prima** della scelta perche' la scelta
     li porta fuori: al pannello servono per scriverli accanto alle spunte, alla
     pagina per ripeterli nella barra sticky accanto al tasto. Sono un unico
     elenco, non due formulazioni che possono divergere. */
  const blockingMessages = useMemo(() => {
    if (!certify || !data?.phone_e164) return [];
    const msgs: string[] = [];
    let anything = false;
    for (const c of customers) {
      const k = custKey(c);
      const s = sel[k] ?? { allBuildings: false, buildings: {} };
      const name = c.ragsoc || c.codcli_for;
      const entries = Object.entries(s.buildings).filter(([, b]) => b.allAssets || b.assets.length > 0);
      if (s.allBuildings || entries.length > 0) {
        anything = true;
        const unresolved = unresolvedFor(c);
        if (!s.allBuildings && unresolved.length > 0 && !dropUnresolved.has(k)) {
          msgs.push(`${name}: ${unresolved.length} impianto/i già concesso/i non è attribuito a un edificio dell'elenco.`);
        }
      } else if (c.granted.certified) {
        anything = true;
        if (!revokeOk.has(k)) msgs.push(`${name}: conferma la revoca per procedere.`);
      }
    }
    if (!anything) msgs.push("Spunta almeno un edificio o un impianto, oppure togli la spunta qui sopra.");
    return msgs;
  }, [certify, customers, sel, data, revokeOk, dropUnresolved, unresolvedFor]);

  const choice: CertificationChoice = useMemo(() => {
    if (!certify || customers.length === 0 || !data?.phone_e164) return NO_CERTIFICATION;

    const tree: CertifyTreeCustomer[] = [];
    const revoke: CertifyCustomerRef[] = [];
    const parts: string[] = [];
    const revokeNames: string[] = [];
    const cleaned: string[] = [];

    for (const c of customers) {
      const k = custKey(c);
      const s = sel[k] ?? { allBuildings: false, buildings: {} };
      const name = c.ragsoc || c.codcli_for;
      /* La scrittura e' autorevole: quello che non viaggia nel payload sparisce
         dal grant. Vale anche per le autorizzazioni morte, che quindi vengono
         ripulite — e va detto adesso, non scoperto dopo. */
      cleaned.push(...deadFor(c));
      if (s.allBuildings) {
        tree.push({ codaz: c.codaz, codcli_for: c.codcli_for, all_buildings: true });
        parts.push(`${name}: tutti gli edifici`);
        continue;
      }
      const entries = Object.entries(s.buildings)
        .filter(([, b]) => b.allAssets || b.assets.length > 0)
        .sort(([a], [b]) => a.localeCompare(b));
      if (entries.length === 0) {
        if (c.granted.certified) {
          revoke.push({ codaz: c.codaz, codcli_for: c.codcli_for });
          revokeNames.push(name);
        }
        continue;
      }
      const buildings: CertifyTreeBuilding[] = entries.map(([edificio, b]) => (
        b.allAssets
          ? { codice_edificio: edificio, all_assets: true }
          : { codice_edificio: edificio, all_assets: false, assets: [...b.assets].sort() }
      ));
      tree.push({ codaz: c.codaz, codcli_for: c.codcli_for, all_buildings: false, buildings });
      const whole = buildings.filter((b) => b.all_assets).length;
      const partial = buildings.filter((b) => !b.all_assets);
      const nAssets = partial.reduce((n, b) => n + (b.assets?.length ?? 0), 0);
      const bits: string[] = [];
      if (whole) bits.push(`${whole} edificio/i intero/i`);
      if (partial.length) bits.push(`${nAssets} impianto/i su ${partial.length} edificio/i`);
      parts.push(`${name}: ${bits.join(" + ")}`);
    }

    if (tree.length === 0 && revoke.length === 0) {
      return { payload: null, summary: null, valid: false, blockers: blockingMessages };
    }
    if (blockingMessages.length > 0) {
      return { payload: null, summary: null, valid: false, blockers: blockingMessages };
    }

    if (revokeNames.length) parts.push(`revoca su ${revokeNames.join(", ")}`);
    if (cleaned.length) {
      parts.push(`rimuove ${cleaned.length} autorizzazione/i non più in anagrafica (${cleaned.join(", ")})`);
    }
    const payload: CertifyCallerPayload = {
      certify: true,
      scope_mode: "tree",
      ...(tree.length ? { tree } : {}),
      ...(revoke.length ? { revoke } : {}),
    };
    return { payload, summary: `${data.phone_e164} — ${parts.join("; ")}`, valid: true, blockers: [] };
  }, [certify, customers, sel, data, blockingMessages, deadFor, revokeOk]);


  /* Pubblicazione verso la pagina. Il confronto e' sul contenuto: `choice` e'
     un oggetto nuovo a ogni render e riemetterlo a vuoto farebbe un ciclo. */
  const lastEmitted = useRef<string>("");
  useEffect(() => {
    const fingerprint = JSON.stringify(choice);
    if (fingerprint === lastEmitted.current) return;
    lastEmitted.current = fingerprint;
    onChange(choice);
  }, [choice, onChange]);

  useEffect(() => () => onChange(NO_CERTIFICATION), [onChange]);

  /* Aperto = sta mostrando la tabella di almeno un cliente. Non serve un
     comando in piu': il momento in cui la larghezza serve e' esattamente
     quello in cui l'operatore apre l'elenco. */
  const expanded = Boolean(data?.phone_e164 && customers.length > 0 && openCust.size > 0);
  const lastExpanded = useRef<boolean | null>(null);
  useEffect(() => {
    if (lastExpanded.current === expanded) return;
    lastExpanded.current = expanded;
    onExpandedChange?.(expanded);
  }, [expanded, onExpandedChange]);

  useEffect(() => () => onExpandedChange?.(false), [onExpandedChange]);

  /* ── Render ──────────────────────────────────────────────────────────── */

  if (!data || !data.phone_e164 || customers.length === 0) return null;

  const anyCertified = customers.some((c) => c.granted.certified);
  const callerTurns = pickCallerTurns(conversation);
  /* Conteggi **per cliente**, mai sommati: due clienti danno due insiemi
     diversi, e "160 di 706" costringeva l'operatore a fare l'aritmetica per
     capire quale dei due fosse troncato — su due clienti di cui uno non lo era
     affatto. Le righe fissate restano fuori dal conteggio, perche' sono fuori
     dalla paginazione: contarle direbbe "3 di 61" avendone visti 2 di quei 61. */
  const paged = (c: CertificationTreeCustomer): number => c.buildings.filter((b) => !b.pinned).length;

  return (
    <div
      className="card"
      data-testid="certification-tree"
      /* Dichiarato sul nodo: chi impagina la pagina (e chi la verifica) non
         deve dedurre dallo stile se il pannello e' aperto. */
      data-expanded={expanded ? "true" : "false"}
    >
      <div className="card-header-accent card-header-accent-brand">
        <span className="material-symbols-outlined" style={{ fontSize: 13 }}>verified_user</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--indaco-gray-600)", textTransform: "uppercase", letterSpacing: ".04em" }}>
          Certificazione del chiamante
        </span>
        {anyCertified && (
          <span className="badge badge-info" style={{ fontSize: 9, marginLeft: "auto" }}>numero già certificato</span>
        )}
      </div>

      {/* Qui c'era un estratto dei turni del chiamante. Era la terza resa
          della stessa conversazione nella stessa schermata — insieme al
          pannello «Conversazione» (le bolle, complete e in ordine) e alla
          trascrizione piatta dentro il riquadro della provenienza — e le tre
          stavano tutte a video contemporaneamente, nelle due colonne. Se ne
          tiene una sola, quella completa; qui resta cio' che serve a decidere
          la certificazione senza rileggere: quanti messaggi ha scritto il
          chiamante, e come arrivarci in un click. */}
      {conversation && (
        <div style={{
          marginBottom: 8, padding: "6px 8px", background: "var(--indaco-gray-50)",
          border: "1px solid var(--indaco-gray-200)", borderRadius: "var(--border-radius-lg)",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14, color: "var(--indaco-gray-600)" }}>forum</span>
          <span style={{ fontSize: 11, color: "var(--indaco-gray-700)" }}>
            {callerTurns.total === 0
              ? "Nessun messaggio del chiamante in questa conversazione."
              : `${callerTurns.total} messagg${callerTurns.total === 1 ? "io" : "i"} del chiamante`}
          </span>
          {onOpenConversation && (
            <button className="btn-link" style={{ marginLeft: "auto", fontSize: 10 }} onClick={onOpenConversation}>
              Conversazione completa
            </button>
          )}
        </div>
      )}

      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={certify}
          aria-label="Modifica la certificazione del chiamante"
          onChange={(e) => setCertify(e.target.checked)}
          style={{ marginTop: 2, accentColor: "var(--indaco-brand-primary)" }}
        />
        <span>
          {anyCertified ? "Modifica la certificazione del numero " : "Certifica il numero "}
          <b>{data.phone_e164}</b>
          <span style={{ display: "block", color: "var(--indaco-gray-500)", fontSize: 11, marginTop: 2 }}>
            Le spunte sono la fotografia di ciò che il chiamante vedrà: quello che togli, non lo vede più.
            Senza questa spunta la certificazione di oggi resta com'è.
          </span>
        </span>
      </label>

      {/* Ricerca sugli edifici: sta sul server, l'elenco locale non e' tutto. */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-500)" }}>search</span>
        <input
          className="form-input"
          aria-label="Cerca edificio"
          placeholder="Cerca edificio per codice, descrizione, indirizzo o comune…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ flex: 1, fontSize: 12, padding: "4px 8px" }}
        />
        {loading && <span className="spinner" style={{ width: 14, height: 14 }} />}
      </div>

      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8, opacity: certify ? 1 : 0.72 }}>
        {customers.map((c) => {
          const k = custKey(c);
          const name = c.ragsoc || c.codcli_for;
          const open = openCust.has(k);
          const s = sel[k] ?? { allBuildings: false, buildings: {} };
          const entries = Object.entries(s.buildings).filter(([, b]) => b.allAssets || b.assets.length > 0);
          const isRevoke = c.granted.certified && !s.allBuildings && entries.length === 0;
          const unresolved = unresolvedFor(c);
          const dead = deadFor(c);
          const rows = sortRows(rowsFor(c), bSort, (row, key) => (
            key === "codice" ? row.codice_edificio
              : key === "descrizione" ? row.descrizione ?? ""
                : key === "indirizzo" ? row.indirizzo ?? ""
                  : key === "comune" ? row.comune ?? ""
                    : key === "impianti" ? row.assets_count ?? -1
                      : ""
          ));
          /* Il server mette in testa le righe fissate, **prima i ticket** e poi le
             gia' concesse. Quell'ordine e' un'informazione, non un caso: se
             l'ordinamento locale lo appiattisse, l'edificio della pratica
             tornerebbe a nascondersi in mezzo a dieci righe concesse mesi fa —
             il difetto che le righe fissate servono a chiudere. Il rango si
             applica quindi **sempre**, e l'ordinamento scelto dall'operatore
             vale dentro ciascun rango (`sort` e' stabile). */
          const rank = (r: BuildingRow): number => (
            (r.pinned_reasons ?? []).includes("ticket") ? 0 : (r.pinned || r.local) ? 1 : 2
          );
          const pinnedFirst = [...rows].sort((a, b) => rank(a) - rank(b));
          const pagedCount = paged(c);
          const totalCount = c.total_buildings ?? pagedCount;
          const alwaysShown = rows.length - pagedCount;
          const searching = debouncedQ.length > 0;
          /* Il contatore non deve contraddire cio' che si vede: con una ricerca
             a vuoto diceva "0 di 0 edifici" mentre una riga fissata era a
             schermo. Le righe sempre visibili si contano a parte. */
          const counter = searching
            ? (pagedCount === 0
              ? `nessun risultato per «${debouncedQ}»`
              : `${pagedCount} di ${totalCount} per «${debouncedQ}»`)
            : `${pagedCount} di ${totalCount} edifici`;

          return (
            <div key={k} style={{ border: "1px solid var(--indaco-gray-200)", borderRadius: "var(--border-radius-lg)", overflow: "hidden" }}>
              {/* ── Livello 1: cliente ── */}
              <div
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
                  background: "var(--indaco-gray-50)", cursor: "pointer",
                }}
                role="button"
                aria-label={`Apri ${name}`}
                onClick={() => setOpenCust((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; })}
              >
                <TriCheckbox
                  state={custState(k)}
                  disabled={!certify}
                  label={`Cliente ${name}`}
                  title="Tutto il cliente, senza restrizioni"
                  onToggle={() => toggleCustomer(k)}
                />
                <span className="material-symbols-outlined" style={{ fontSize: 16, color: "var(--indaco-gray-600)" }}>
                  {open ? "expand_more" : "chevron_right"}
                </span>
                <b style={{ fontSize: 12 }}>{name}</b>
                <code style={{ fontSize: 10, color: "var(--indaco-gray-500)" }}>{c.codaz}/{c.codcli_for}</code>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--indaco-gray-600)", whiteSpace: "nowrap" }}>
                  {counter}
                  {alwaysShown > 0 && (
                    <span style={{ color: "var(--indaco-gray-500)" }}> · {alwaysShown} sempre in cima</span>
                  )}
                </span>
              </div>

              {/* Cosa e' concesso oggi, in chiaro: il punto di partenza delle spunte. */}
              <div style={{ padding: "4px 8px", fontSize: 11, borderTop: "1px solid var(--indaco-gray-100)", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ color: "var(--indaco-gray-600)" }}>Oggi vede:</span>
                {!c.granted.certified && <span>nulla — il numero non è certificato su questo cliente</span>}
                {c.granted.certified && c.granted.all_buildings && <b>tutto il cliente</b>}
                {c.granted.certified && !c.granted.all_buildings && (
                  <>
                    {c.granted.buildings.length === 0 && c.granted.assets.length === 0 && <b>tutto il cliente</b>}
                    {c.granted.buildings.map((b) => (
                      <span key={b} className="badge badge-info" style={{ fontSize: 9 }}>{buildingLabel(k, b)} — intero</span>
                    ))}
                    {c.granted.assets.filter((a) => !dead.includes(a)).map((a) => (
                      <span key={a} className="badge badge-neutral" style={{ fontSize: 9 }}>{assetLabel(k, a)}</span>
                    ))}
                  </>
                )}
                {c.ticket_buildings && c.ticket_buildings.length > 0 && (
                  <span style={{ marginLeft: "auto", color: "var(--indaco-gray-600)" }}>
                    Ticket della pratica: {c.ticket_buildings.join(", ")}
                  </span>
                )}
              </div>

              {/* Autorizzazioni morte: concesse su impianti che l'anagrafica non ha
                  piu'. Non autorizzano nulla e nessuna pagina dell'albero potra'
                  mai contenerle, quindi **non bloccano** l'approvazione: se lo
                  facessero, la guardia sugli impianti non ancora attribuiti
                  diventerebbe un muro, in attesa di una riga che non arrivera'.
                  Si mostrano per quello che sono, e si dice che la prossima
                  approvazione le ripulisce. */}
              {dead.length > 0 && (
                <div className="alert alert-info" style={{ fontSize: 11, margin: "6px 8px" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>link_off</span>
                  <span style={{ flex: 1 }}>
                    {dead.length} autorizzazione/i su impianti <b>che non esistono più in anagrafica</b>{" "}
                    ({dead.join(", ")}): non concedono nulla, né oggi né dopo.
                    {certify
                      ? <> Approvando con queste spunte <b>verranno rimosse</b> dalla certificazione.</>
                      : <> Restano lì finché non si rifà la certificazione.</>}
                  </span>
                </div>
              )}

              {isRevoke && certify && (
                <div className="alert alert-danger" style={{ fontSize: 11, margin: "6px 8px" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>report</span>
                  <span style={{ flex: 1 }}>
                    Nessuna spunta su <b>{name}</b>: alla prossima chiamata il numero <b>non vedrà più nulla</b> di
                    questo cliente. È una revoca, non una concessione totale.
                    <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        aria-label={`Conferma revoca ${name}`}
                        checked={revokeOk.has(k)}
                        onChange={() => setRevokeOk((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; })}
                        style={{ accentColor: "var(--indaco-danger)" }}
                      />
                      <span>Confermo la revoca della certificazione su {name}</span>
                    </label>
                  </span>
                </div>
              )}

              {certify && unresolved.length > 0 && (
                <div className="alert alert-warning" style={{ fontSize: 11, margin: "6px 8px" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>warning</span>
                  <span style={{ flex: 1 }}>
                    {unresolved.length} impianto/i già concesso/i ({unresolved.join(", ")}) non è ancora attribuito a un
                    edificio di questo elenco: apri o cerca il suo edificio per governarlo. Finché resta così, la
                    ricertificazione lo toglierebbe.
                    <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        aria-label={`Togli impianti non attribuiti ${name}`}
                        checked={dropUnresolved.has(k)}
                        onChange={() => setDropUnresolved((p) => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; })}
                        style={{ accentColor: "var(--indaco-warning)" }}
                      />
                      <span>So che li sto togliendo e voglio procedere</span>
                    </label>
                  </span>
                </div>
              )}

              {/* ── Livello 2: edifici ── */}
              {open && c.truncated && (
                <div className="alert alert-warning" style={{ fontSize: 11, margin: "6px 8px" }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 15 }}>warning</span>
                  <span>
                    Elenco parziale di <b>{name}</b>: sono mostrati <b>{pagedCount}</b> edifici su <b>{totalCount}</b>.
                    Quello che non vedi non è selezionabile da qui, e ordinamento e ricerca locale valgono sulle righe
                    caricate. Usa la ricerca oppure carica altri edifici.
                  </span>
                </div>
              )}

              {open && (
                <div className="table-container">
                  {/* Spuntare i visibili: l'unica alternativa, su 645 edifici, era
                      una per una oppure tutto il cliente. */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 8px", flexWrap: "wrap" }}>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ fontSize: 11 }}
                      disabled={!certify || s.allBuildings || pinnedFirst.length === 0}
                      onClick={() => setShown(k, pinnedFirst.map((r) => r.codice_edificio), true)}
                    >
                      {/* Gli stessi numeri del contatore qui sopra, scomposti:
                          "101" contro "100 di 645" costringeva a fare la somma
                          per capire da dove venisse la riga in piu'. */}
                      {alwaysShown > 0
                        ? `Spunta ${pagedCount} caricati + ${alwaysShown} fissat${alwaysShown === 1 ? "o" : "i"}`
                        : `Spunta i ${pagedCount} edifici mostrati`}
                    </button>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ fontSize: 11 }}
                      disabled={!certify || s.allBuildings || pinnedFirst.length === 0}
                      onClick={() => setShown(k, pinnedFirst.map((r) => r.codice_edificio), false)}
                    >
                      Togli le spunte dai mostrati
                    </button>
                    <span style={{ fontSize: 10, color: "var(--indaco-gray-600)" }}>
                      solo questi {pinnedFirst.length}, non tutti i {totalCount} edifici del cliente
                    </span>
                    {bulkFeedback[k] && (
                      <span style={{ fontSize: 10, color: "var(--indaco-success)", display: "flex", alignItems: "center", gap: 3 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 13 }}>check_circle</span>
                        {bulkFeedback[k]}
                      </span>
                    )}
                  </div>
                  {/* `table-layout: fixed` + un'ultima colonna elastica: le colonne
                      tengono la loro larghezza massima e lo spazio che avanza si
                      raccoglie **a destra**, invece di gonfiare "Indirizzo" fino a
                      lasciare mezzo schermo di bianco fra la via e il numero di
                      impianti. Le informazioni che si leggono insieme restano
                      vicine. `minWidth` fa scattare lo scorrimento orizzontale del
                      contenitore sotto i ~900px, invece di schiacciare le colonne. */}
                  <table style={{ tableLayout: "fixed", minWidth: 840 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 26, padding: "4px 6px" }} />
                        <SortTh label="Edificio" sortKey="codice" sort={bSort} setSort={setBSort}
                          second={{ label: "descr.", sortKey: "descrizione" }} style={{ width: 240 }} />
                        <SortTh label="Indirizzo" sortKey="indirizzo" sort={bSort} setSort={setBSort}
                          second={{ label: "comune", sortKey: "comune" }} style={{ width: 300 }} />
                        <SortTh label="Imp." sortKey="impianti" sort={bSort} setSort={setBSort} style={{ width: 56 }} />
                        <th style={{ padding: "4px 6px", fontSize: 10, width: 210 }}>Cosa concedi</th>
                        <th style={{ width: "auto" }} />
                      </tr>
                    </thead>
                    <tbody>
                      {pinnedFirst.length === 0 && (
                        <tr><td colSpan={6} style={{ fontSize: 11, color: "var(--indaco-gray-500)" }}>Nessun edificio per questa ricerca</td></tr>
                      )}
                      {pinnedFirst.map((b) => {
                        const key = bKey(k, b.codice_edificio);
                        const bOpen = openBuild.has(key);
                        const st = buildState(k, b.codice_edificio);
                        const bs = s.buildings[b.codice_edificio];
                        const aState = assets[key];
                        const notice = assetNotice[key];
                        const q = (assetQuery[key] ?? "").trim().toLowerCase();
                        const filtered = (aState?.rows ?? []).filter((a) => !q || [
                          a.codice_impianto, a.desc_tipo, a.descrizione, a.desc_prest, a.potenza,
                        ].some((v) => String(v ?? "").toLowerCase().includes(q)));
                        const aRows = sortRows(filtered, aSort, (row, sk) => (
                          sk === "codice" ? row.codice_impianto
                            : sk === "tipo" ? row.desc_tipo ?? ""
                              : sk === "descrizione" ? row.descrizione ?? ""
                                : sk === "prestazione" ? row.desc_prest ?? ""
                                  : sk === "potenza" ? row.potenza ?? ""
                                    : ""
                        ));
                        // Il motivo del fissaggio lo dichiara il server: qui si legge, non si deduce.
                        const isTicket = (b.pinned_reasons ?? []).includes("ticket");
                        const grantedHere = (b.pinned_reasons ?? []).includes("granted");
                        const allIndividually = Boolean(
                          bs && !bs.allAssets && aState && !aState.loading && !aState.truncated
                          && aState.rows.length > 0 && bs.assets.length === aState.rows.length,
                        );

                        return (
                          <Fragment key={key}>
                            <tr
                              /* La riga che l'operatore cerca e' quella della pratica:
                                 un fondo tenue la rende evidente senza leggere i badge. */
                              style={isTicket ? { background: "var(--indaco-warning-light)" } : undefined}
                              onClick={() => setOpenBuild((p) => { const n = new Set(p); if (n.has(key)) n.delete(key); else n.add(key); return n; })}
                            >
                              <td style={{ padding: "4px 6px" }} onClick={(e) => e.stopPropagation()}>
                                <TriCheckbox
                                  state={st}
                                  disabled={!certify || s.allBuildings}
                                  label={`Edificio ${b.codice_edificio}`}
                                  title={s.allBuildings ? "Tutto il cliente è già concesso: togli la spunta sul cliente per scegliere i singoli edifici" : "Edificio intero, impianti futuri compresi"}
                                  onToggle={() => toggleBuilding(k, b.codice_edificio)}
                                />
                              </td>
                              <td style={{ padding: "4px 6px", overflowWrap: "anywhere" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 2, fontSize: 12 }}>
                                  <span className="material-symbols-outlined" style={{ fontSize: 13, color: "var(--indaco-gray-600)" }}>
                                    {bOpen ? "expand_more" : "chevron_right"}
                                  </span>
                                  <b>{b.codice_edificio}</b>
                                </div>
                                {/* Perché la riga è qui: "è l'edificio di questa pratica" e
                                    "è già concesso" sono informazioni diverse per chi decide,
                                    e possono valere entrambe per lo stesso edificio. */}
                                {(isTicket || grantedHere || b.pinned || b.local) && (
                                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 1 }}>
                                    {isTicket && (
                                      <span className="badge badge-warning" style={{ fontSize: 9 }}
                                        title="È l'edificio del ticket di questa pratica">della pratica</span>
                                    )}
                                    {grantedHere && (
                                      <span className="badge badge-info" style={{ fontSize: 9 }}
                                        title="Già concesso a questo numero: la riga resta visibile anche fuori dalla ricerca">già concesso</span>
                                    )}
                                    {b.pinned && !isTicket && !grantedHere && (
                                      <span className="badge badge-neutral" style={{ fontSize: 9 }}
                                        title="Riga fissata dal server: resta visibile anche fuori dalla ricerca">fissato</span>
                                    )}
                                    {b.local && (
                                      <span className="badge badge-neutral" style={{ fontSize: 9 }}
                                        title="Selezionato in questa sessione, fuori dalla ricerca corrente">fuori elenco</span>
                                    )}
                                  </div>
                                )}
                                {b.descrizione && (
                                  <div style={{ fontSize: 11, color: "var(--indaco-gray-700)", marginTop: 1 }}>{b.descrizione}</div>
                                )}
                              </td>
                              <td style={{ padding: "4px 6px", fontSize: 11, overflowWrap: "anywhere" }}>
                                <div>{b.indirizzo || "—"}</div>
                                {b.comune && <div style={{ color: "var(--indaco-gray-600)" }}>{b.comune}</div>}
                              </td>
                              <td style={{ padding: "4px 6px", fontSize: 12 }}>{b.assets_count ?? "—"}</td>
                              <td style={{ padding: "4px 6px", fontSize: 11 }}>
                                {/* La differenza che si manifesta nel tempo, scritta a parole:
                                    due righe brevi invece di una frase che va a capo da sola. */}
                                {st === "on" && (
                                  <span style={{ color: "var(--indaco-success)" }}
                                    title="Grant di edificio: comprende anche gli impianti che verranno aggiunti in futuro">
                                    <b>Edificio intero</b>
                                    <span style={{ display: "block", fontSize: 10 }}>inclusi gli impianti futuri</span>
                                  </span>
                                )}
                                {st === "mixed" && bs && (
                                  <span style={{ color: "var(--indaco-warning)" }}
                                    title="Lista esplicita di impianti: congela l'insieme, gli impianti aggiunti in futuro non saranno inclusi">
                                    <b>{bs.assets.length} impianto/i scelti</b>
                                    <span style={{ display: "block", fontSize: 10 }}>esclusi gli impianti futuri</span>
                                  </span>
                                )}
                                {/* `off` non scrive nulla: "niente" ripetuto su cento
                                    righe e' rumore, e una cella vuota lo dice uguale. */}
                              </td>
                              <td />
                            </tr>

                            {/* ── Livello 3: impianti ── */}
                            {bOpen && (
                              <tr>
                                <td
                                  colSpan={6}
                                  style={{
                                    background: "var(--indaco-gray-50)",
                                    padding: "8px 12px 10px 26px",
                                    borderLeft: "3px solid var(--indaco-brand-primary)",
                                  }}
                                >
                                  {aState?.loading && <span className="spinner" style={{ width: 14, height: 14 }} />}
                                  {aState?.error && (
                                    <span style={{ fontSize: 11, color: "var(--indaco-danger)" }}>Impianti non caricati: {aState.error}</span>
                                  )}
                                  {aState && !aState.loading && !aState.error && (
                                    <>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                        <input
                                          className="form-input"
                                          aria-label={`Cerca impianto in ${b.codice_edificio}`}
                                          placeholder="Filtra impianti di questo edificio…"
                                          value={assetQuery[key] ?? ""}
                                          onChange={(e) => setAssetQuery((p) => ({ ...p, [key]: e.target.value }))}
                                          style={{ flex: 1, fontSize: 11, padding: "3px 6px" }}
                                        />
                                        <span style={{ fontSize: 10, color: "var(--indaco-gray-600)" }}>
                                          {aRows.length} di {aState.rows.length} impianti
                                        </span>
                                      </div>
                                      {aState.truncated && (
                                        <div className="alert alert-warning" style={{ fontSize: 10, marginBottom: 4 }}>
                                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>warning</span>
                                          <span>Elenco impianti parziale: non tutti gli impianti dell'edificio sono qui.</span>
                                        </div>
                                      )}
                                      {notice && (
                                        <div className="alert alert-warning" style={{ fontSize: 10, marginBottom: 4 }}>
                                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>warning</span>
                                          <span>{notice}</span>
                                        </div>
                                      )}
                                      {allIndividually && (
                                        <div className="alert alert-info" style={{ fontSize: 10, marginBottom: 4 }}>
                                          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>info</span>
                                          <span style={{ flex: 1 }}>
                                            Hai spuntato tutti gli impianti di oggi, ma non l'edificio: gli impianti che
                                            verranno aggiunti domani <b>non</b> saranno inclusi.
                                          </span>
                                          <button
                                            className="btn btn-outline btn-sm"
                                            style={{ fontSize: 10 }}
                                            onClick={() => promoteToWholeBuilding(k, b.codice_edificio)}
                                          >
                                            Concedi l'edificio intero
                                          </button>
                                        </div>
                                      )}
                                      <table style={{ tableLayout: "fixed", minWidth: 700 }}>
                                        <thead>
                                          <tr>
                                            <th style={{ width: 26, padding: "3px 6px" }} />
                                            <SortTh label="Impianto" sortKey="codice" sort={aSort} setSort={setASort} style={{ width: 120 }} />
                                            <SortTh label="Tipo" sortKey="tipo" sort={aSort} setSort={setASort}
                                              second={{ label: "descr.", sortKey: "descrizione" }} style={{ width: 320 }} />
                                            <SortTh label="Prestazione" sortKey="prestazione" sort={aSort} setSort={setASort} style={{ width: 220 }} />
                                            <th style={{ width: "auto" }} />
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {aRows.length === 0 && (
                                            <tr><td colSpan={5} style={{ fontSize: 11, color: "var(--indaco-gray-500)" }}>Nessun impianto</td></tr>
                                          )}
                                          {aRows.map((a) => (
                                            <tr key={a.codice_impianto}>
                                              <td style={{ padding: "3px 6px" }}>
                                                <TriCheckbox
                                                  state={assetChecked(k, b.codice_edificio, a.codice_impianto) ? "on" : "off"}
                                                  disabled={!certify || s.allBuildings}
                                                  label={`Impianto ${a.codice_impianto}`}
                                                  onToggle={() => toggleAsset(k, b.codice_edificio, a.codice_impianto)}
                                                />
                                              </td>
                                              <td style={{ padding: "3px 6px", fontSize: 12 }}><b>{a.codice_impianto}</b></td>
                                              <td style={{ padding: "3px 6px", fontSize: 12, overflowWrap: "anywhere" }}>
                                                {a.desc_tipo || "—"}
                                                {potenzaLabel(a) && (
                                                  <span style={{ fontSize: 10, color: "var(--indaco-gray-600)", marginLeft: 4 }}
                                                    title="Potenza come registrata in anagrafica: l'unità di misura non è un dato disponibile">
                                                    {potenzaLabel(a)}
                                                  </span>
                                                )}
                                                {a.descrizione && (
                                                  <div style={{ fontSize: 11, color: "var(--indaco-gray-600)" }}>{a.descrizione}</div>
                                                )}
                                              </td>
                                              <td style={{ padding: "3px 6px", fontSize: 11, overflowWrap: "anywhere" }}>{a.desc_prest || "—"}</td>
                                              <td />
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      {/* L'anagrafica non registra lo stato: dirlo, invece di lasciar
                                          credere che sia un elenco di impianti attivi. */}
                                      <div style={{ fontSize: 10, color: "var(--indaco-gray-600)", marginTop: 4 }}>
                                        L'anagrafica non registra lo stato degli impianti: da qui non si può sapere se un
                                        impianto è dismesso.
                                      </div>
                                    </>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {c.truncated && (
                    <div style={{ padding: "6px 8px", display: "flex", alignItems: "center", gap: 8 }}>
                      <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={loadMore} disabled={loadingMore}>
                        {loadingMore ? "Carico…" : `Carica altri edifici (${c.buildings.length} di ${c.total_buildings ?? c.buildings.length})`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {certify && blockingMessages.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 2 }}>
          {blockingMessages.map((m) => (
            <span key={m} style={{ fontSize: 11, color: "var(--indaco-warning)" }}>{m}</span>
          ))}
        </div>
      )}

      {certify && choice.summary && (
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--indaco-gray-700)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", color: "var(--indaco-brand-primary)" }}>check_circle</span>{" "}
          Con l'approvazione: <b>{choice.summary}</b>
        </div>
      )}
    </div>
  );
}
